import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import { RowActionsMenu } from 'src/components/shared/grid/RowActionsMenu'
import type { DocusignTemplate } from 'src/interfaces/docusignTemplate'
import type { MenuActionItem } from 'src/interfaces/ui'
import { getDocusignTemplateDeletingId } from 'src/selectors/docusignTemplate'
import { GSIcon } from 'src/components/shared/GSIcon'

const EDIT_DISABLED_REASON = 'Editing a DocuSign template is not supported by the admin API yet.'

interface Props {
  template: DocusignTemplate
  onDelete(template: DocusignTemplate): void
}

const DocusignTemplateActionsCellComponent = ({ template, onDelete }: Props) => {
  const deletingId = useSelector(getDocusignTemplateDeletingId)
  const isDeleting = deletingId === template.id

  const items = useMemo<MenuActionItem[]>(() => {
    return [
      {
        label: 'Edit',
        icon: <GSIcon iconName="gs_pencil" size={20} css={tw`text-shades-1000`} />,
        disabled: true,
        disabledReason: EDIT_DISABLED_REASON,
        dataTestId: `docusign-templates-list-edit-${template.id}`,
        onClick: () => {},
      },
      {
        label: isDeleting ? 'Deleting…' : 'Delete',
        icon: <GSIcon iconName="gs_trash_can" size={20} css={tw`text-critical-500`} />,
        color: 'var(--mui-critical-500)',
        disabled: isDeleting,
        dataTestId: `docusign-templates-list-delete-${template.id}`,
        onClick: () => {
          onDelete(template)
        },
      },
    ]
  }, [template, isDeleting, onDelete])

  return (
    <RowActionsMenu
      items={items}
      ariaLabel="DocuSign template actions"
      dataTestId={`docusign-templates-list-actions-${template.id}`}
    />
  )
}

export const DocusignTemplateActionsCell = memo(DocusignTemplateActionsCellComponent)
